import { formatAud, PRODUCT_CONFIG } from "@stb/product-config"
import { createFileRoute } from "@tanstack/react-router"
import { Check, CreditCard, FileText, Hourglass, Route as RouteIcon, Sparkles } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import { localizeHref } from "@/core/i18n/locale-context"
import { gateCta } from "@/core/launch"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb, CtaBand, FaqAccordion, SimpleTable } from "../../components/landing"
import { HeroBand, StatRow } from "../../components/landing/page-parts"
import { IconTile } from "../../components/landing/product-art"
import { englishDateToZh } from "../../content/zh/dates"

/** Chinese version of /pricing (D-WEB-LANG-SCOPE). Prices come from @stb/product-config, the same source as English. */
const REVIEWED = englishDateToZh("28 October 2026")
const TRIAL_HREF = "/login"
const planCta = (label: string) => gateCta({ href: TRIAL_HREF, label }, "zh")

export const pricingZhSeo = () =>
  buildHead({
    title: "价格与套餐",
    description: `为个体经营者准备的免费版、Logbook 和 Pro 套餐。新账户可免费试用 Logbook 或 Pro ${TRIAL_DAYS} 天，无需绑定银行卡，按月或按年付费。`,
    path: "/pricing",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "价格与套餐" }])],
  })

export const Route = createFileRoute("/_brand-layout/zh/pricing")({
  head: () => {
    const { meta, links, scripts } = pricingZhSeo()
    return { meta, links, scripts }
  },
  component: PricingZhPage,
})

const { free, logbook, pro } = PRODUCT_CONFIG.plans
const tripsLine = (n: number | null) =>
  n === null ? "不限数量的 GPS 行程" : `每月 ${n} 个 GPS 行程`
const cell = (n: number | null) => (n === null ? "不限" : `每月 ${n} 个`)

interface Plan {
  name: string
  icon: LucideIcon
  tone: "primary" | "success"
  price: string
  per?: string
  yearly?: string
  note?: string
  badge?: string
  features: string[]
  cta: string
  primary?: boolean
}

const PLANS: Plan[] = [
  {
    name: "免费版",
    icon: Sparkles,
    tone: "primary",
    price: formatAud(free.monthlyCents),
    note: "试用结束后，如果你没有订阅",
    features: [
      `每月 ${free.tripsMonthly} 个 GPS 行程`,
      `每月 ${free.invoicesMonthly} 张发票`,
      `每月 ${free.aiReadsMonthly} 次 AI 读取收据`,
      `${free.vehicles} 辆车`,
    ],
    cta: "免费开始",
  },
  {
    name: "Logbook",
    icon: RouteIcon,
    tone: "success",
    price: formatAud(logbook.monthlyCents),
    per: "/ 月",
    yearly: `或 ${formatAud(logbook.yearlyCents)} / 年`,
    features: [
      tripsLine(logbook.tripsMonthly),
      "cents per km 法和 logbook 法",
      "Logbook 报告和 CSV 导出",
      `${logbook.vehicles} 辆车，每多一辆 ${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)} / 月`,
    ],
    cta: "选择 Logbook",
  },
  {
    name: "Pro",
    icon: FileText,
    tone: "primary",
    price: formatAud(pro.monthlyCents),
    per: "/ 月",
    yearly: `或 ${formatAud(pro.yearlyCents)} / 年`,
    badge: "含开发票功能",
    features: [
      "包含 Logbook 的全部功能",
      "不限数量的 Tax Invoice",
      `每月 ${pro.aiReadsMonthly} 次 AI 读取收据`,
      "每张发票含 GST（商品及服务税）计算",
      `${pro.vehicles} 辆车，每多一辆 ${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)} / 月`,
    ],
    cta: "选择 Pro",
    primary: true,
  },
]

const STEPS = [
  ["告诉我们你的需要", "只记录自己的车，选 Logbook；其他情况选 Pro。无需绑定银行卡。"],
  [`免费用 ${TRIAL_DAYS} 天`, `你选的套餐里的全部功能，免费用 ${TRIAL_DAYS} 天。`],
  ["订阅，或继续用免费版", "不订阅就转为免费版。你的记录仍可查看和导出。"],
] as const

const BILLING_FAQ = [
  {
    question: "试用结束后会怎样？",
    answer: "不订阅就转为免费版。你的记录仍可查看和导出。",
  },
  {
    question: "可以随时取消吗？",
    answer: "可以。套餐在我们的网站上购买和管理，所以也在网站上取消。",
  },
]

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

export function PricingZhPage() {
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "价格与套餐" }]} />
      <HeroBand>
        {PRODUCT_CONFIG.pricesDraft && (
          <p className="bg-accent text-accent-foreground inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
            <Hourglass className="size-3.5" aria-hidden="true" />
            草案价格，非最终价格。
          </p>
        )}
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">选一个适合你工作的套餐</h1>
        <p className="text-muted-foreground max-w-[60ch] text-lg">
          新账户可以免费试用 Logbook 或 Pro {TRIAL_DAYS} 天，具体看你注册时的选择。无需绑定银行卡。
        </p>
        <StatRow
          label="要点"
          stats={[
            { value: `${TRIAL_DAYS} 天`, label: "免费试用", icon: Sparkles, tone: "primary" },
            { value: "无需银行卡", label: "即可开始", icon: CreditCard, tone: "success" },
          ]}
        />
      </HeroBand>

      <section aria-label="套餐" role="region" className="px-band py-8">
        <div className="grid gap-4 md:grid-cols-3">
          {PLANS.map((plan) => (
            <article
              key={plan.name}
              className={`bg-card grid content-start gap-3 rounded-xl border p-6 ${plan.primary ? "border-primary border-2" : "border-border"}`}
            >
              <div className="flex flex-wrap items-center gap-3">
                <IconTile icon={plan.icon} tone={plan.tone} />
                <h2 className="text-xl font-semibold">{plan.name}</h2>
                {plan.badge && (
                  <span className="bg-primary/10 text-primary rounded-full px-3 py-0.5 text-xs font-semibold">
                    {plan.badge}
                  </span>
                )}
              </div>
              {plan.note && <p className="text-muted-foreground text-sm">{plan.note}</p>}
              <p>
                <span className="text-4xl font-bold">{plan.price}</span>
                {plan.per && <span className="text-muted-foreground"> {plan.per}</span>}
              </p>
              {plan.yearly && <p className="text-muted-foreground text-sm">{plan.yearly}</p>}
              <ul className="grid gap-2 text-sm">
                {plan.features.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="text-primary mt-0.5 size-4 shrink-0" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href={planCta(plan.cta).href}
                className={`focus-visible:ring-ring mt-2 inline-flex min-h-11 items-center justify-center rounded-md px-4 font-medium outline-none focus-visible:ring-2 ${plan.primary ? "bg-primary text-primary-foreground" : "border-border border"}`}
              >
                {planCta(plan.cta).label}
              </a>
            </article>
          ))}
        </div>
        <p className="text-muted-foreground mt-4 text-sm">
          套餐在我们的网站上购买。iPhone 和 Android 应用不收款。
        </p>
        <aside
          role="note"
          aria-label="新客户优惠"
          className="border-border bg-card mt-4 rounded-lg border border-dashed p-4 text-sm"
        >
          <b>新客户优惠</b>
          <p className="text-muted-foreground mt-1">
            面向新客户的限时试行活动，适用于 Pro 月付套餐。优惠在免费试用结束后开始，不适用于
            Logbook
            或年付套餐，标价不变。2027年3月31日（澳大利亚东部时间）或之前开始付费的新客户均可参加。已经享有该优惠的客户，6
            个月内继续享有。
          </p>
        </aside>
      </section>

      <section aria-labelledby="team-plan-title" className="px-band py-8">
        <div className="border-border bg-card grid gap-4 rounded-xl border p-6 md:grid-cols-[1fr_1.4fr] md:items-start">
          <div className="grid content-start gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="team-plan-title" className="text-xl font-semibold">
                团队套餐
              </h2>
              <span className="bg-muted text-muted-foreground rounded-full px-3 py-0.5 text-xs font-semibold">
                即将推出
              </span>
            </div>
            <p>
              <span className="text-4xl font-bold">
                {formatAud(PRODUCT_CONFIG.team.baseMonthlyCents)}
              </span>
              <span className="text-muted-foreground"> / 月</span>
            </p>
            <p className="text-muted-foreground text-sm">
              或 {formatAud(PRODUCT_CONFIG.team.baseYearlyCents)} / 年
            </p>
          </div>
          <ul className="grid gap-2 text-sm">
            {[
              `除账户所有者外，另含 ${PRODUCT_CONFIG.team.includedSeats} 个成员席位`,
              `额外席位每个 ${formatAud(PRODUCT_CONFIG.team.extraSeatMonthlyCents)} / 月，或 ${formatAud(PRODUCT_CONFIG.team.extraSeatYearlyCents)} / 年`,
            ].map((f) => (
              <li key={f} className="flex gap-2">
                <Check className="text-primary mt-0.5 size-4 shrink-0" aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-muted/40 border-border px-band border-y py-8">
        <h2 className="text-2xl font-semibold">免费试用怎么用</h2>
        <ol className="mt-4 grid gap-4 md:grid-cols-3">
          {STEPS.map(([title, body], i) => (
            <li key={title} className="bg-card border-border flex gap-3 rounded-lg border p-4">
              <span
                className="bg-primary text-primary-foreground grid size-8 shrink-0 place-items-center rounded-full font-bold"
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-muted-foreground text-sm">{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="px-band py-8">
        <h2 className="mb-4 text-2xl font-semibold">套餐对比</h2>
        <SimpleTable
          caption="套餐对比"
          columns={["包含内容", "免费版", "Logbook", "Pro"]}
          rows={[
            {
              header: "GPS 行程",
              cells: [cell(free.tripsMonthly), cell(logbook.tripsMonthly), cell(pro.tripsMonthly)],
            },
            {
              header: "Tax Invoice",
              cells: [cell(free.invoicesMonthly), "Logbook 不含", cell(pro.invoicesMonthly)],
            },
            {
              header: "AI 读取收据",
              cells: [cell(free.aiReadsMonthly), "Logbook 不含", cell(pro.aiReadsMonthly)],
            },
            {
              header: "车辆",
              cells: [
                String(free.vehicles),
                `${logbook.vehicles}（每多一辆 +${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)}）`,
                `${pro.vehicles}（每多一辆 +${formatAud(PRODUCT_CONFIG.addons.vehicle.monthlyCents)}）`,
              ],
            },
            { header: "Logbook 报告、CSV", cells: ["额度内", "有", "有"] },
            { header: "每张发票含 GST 计算", cells: ["—", "—", "有"] },
          ]}
        />
        <p className="text-muted-foreground mt-3 text-sm">
          最近复核 {REVIEWED}。Logbook 是只含里程功能的套餐；要开发票和读收据，请升级到 Pro。
        </p>
      </section>

      <section className="px-band py-8">
        <h2 className="mb-4 text-2xl font-semibold">付费常见问题</h2>
        <FaqAccordion items={BILLING_FAQ} />
        <p>
          更多解答见
          <a href={localizeHref("/faq", "zh")} className={linkClass}>
            常见问题
          </a>
          页面。
        </p>
      </section>

      <CtaBand
        title={`先免费试用 ${TRIAL_DAYS} 天`}
        description="无需绑定银行卡。"
        ctaLabel="开始免费试用"
        ctaHref={TRIAL_HREF}
      />
    </>
  )
}
